import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { usePermissions } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import {
  cleanHoldingCompanies,
  deleteHoldingCompany,
  fetchHoldingCompanies,
} from '../../redux/modules/holdingCompany/holdingCompany.actions';
import { holdingCompanySelector } from '../../redux/modules/holdingCompany/holdingCompany.selectors';
import { HoldingCompany } from '../../types/holdingCompany';
import * as permissions from '../../utils/permissions';
import { HoldingCompanyDialog } from './HoldingCompanyDialog';

export function HoldingCompanyList() {
  const dispatch = useAppDispatch();
  const tableRef = useRef<TableRef>(null);
  const [open, setOpen] = useState(false);
  const [editValue, setEditValue] = useState<HoldingCompany | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const canDelete = usePermissions({ items: [permissions.HoldingCompany.delete] });
  const canUpdate = usePermissions({ items: [permissions.HoldingCompany.update] });
  const [columns, setColumns] = useState<GridColDef[]>([
    {
      field: 'logoUrl',
      headerName: 'Logo',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Avatar src={params.row.logoUrl} alt="" sx={{ width: 40, height: 40, bgcolor: '#ccc' }} variant="rounded">
          {params.row.logoUrl ? null : params.row.nameEn?.[0] ?? '—'}
        </Avatar>
      ),
    },
    {
      field: 'ticker',
      headerName: 'Ticker',
      width: 100,
    },
    {
      field: 'nameEn',
      headerName: 'Name En',
      flex: 1,
    },
    {
      field: 'nameAr',
      headerName: 'Name Ar',
      flex: 1,
    },
    {
      field: 'website',
      headerName: 'Website',
      flex: 1,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      width: 100,
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      width: 100,
    },
  ]);

  const { totalHoldingCompanies, holdingCompanies, fetchStatus, deleteStatus } = useSelector(holdingCompanySelector);

  const onDeleteHoldingCompany = (id: number) => {
    dispatch(deleteHoldingCompany(id));
  };

  const onEditHoldingCompany = (row: HoldingCompany): void => {
    setEditValue(row);
    setOpen(true);
  };

  const onAddNew = (): void => {
    setOpen(true);
  };

  const handleClose = (isReload: boolean) => {
    if (isReload) {
      tableRef?.current?.reload();
    }

    setEditValue(null);
    setOpen(false);
  };

  useEffect(
    () => () => {
      dispatch(cleanHoldingCompanies());
    },
    [],
  );

  useEffect(() => {
    if (deleteStatus === fetchStatuses.success) {
      handleClose(true);
      enqueueSnackbar('Company deleted!', { variant: 'success' });
    }
    if (deleteStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Company deletion error!', { variant: 'error' });
    }
  }, [deleteStatus]);

  useEffect(() => {
    const actionColumn = {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => {
        const actions: React.ReactElement<GridActionsCellItemProps>[] = [];

        if (canDelete) {
          actions.push(
            <GridActionsCellItem
              key={`holding-company-list-delete-${params.row.id}`}
              icon={<DeleteOutlineOutlinedIcon />}
              label="Link"
              onClick={() => onDeleteHoldingCompany(params.row.id)}
            />,
          );
        }

        if (canUpdate) {
          actions.push(
            <GridActionsCellItem
              key={`holding-company-list-update-${params.row.id}`}
              icon={<EditOutlinedIcon />}
              label="Actions"
              onClick={() => onEditHoldingCompany(params.row)}
            />,
          );
        }

        return actions;
      },
    };

    if (canDelete || canUpdate) {
      setColumns([actionColumn, ...columns]);
    }
  }, [canDelete, canUpdate]);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Companies &nbsp;
          <Fab color="primary" size="small" aria-label="Create Company" onClick={onAddNew}>
            <AddIcon />
          </Fab>
        </Typography>
      </Box>

      <HoldingCompanyDialog open={open} onClose={handleClose} initialValue={editValue} />

      <Table
        ref={tableRef}
        fetchItems={fetchHoldingCompanies}
        rows={holdingCompanies}
        columns={columns}
        loading={fetchStatus === fetchStatuses.pending}
        rowCount={totalHoldingCompanies}
      />
    </Container>
  );
}
